/**
 * Layout Service
 * Port-aware auto-layout with two universal rules:
 *
 * Rule 1 – SATELLITE connections (special-handle target):
 *   Look up the actual `position` field of the TARGET port on the satellite node.
 *   - port.position === 'bottom'  →  satellite placed ABOVE  parent  (bottom port faces ↓ toward parent)
 *   - port.position === 'top'     →  satellite placed BELOW  parent  (top port faces ↑ toward parent)
 *   - port.position === 'left'    →  satellite placed to the LEFT  of parent
 *   - port.position === 'right'   →  satellite placed to the RIGHT of parent
 *   Multiple satellites from the same parent are spread in their respective direction.
 *
 * Rule 2 – FLOW connections (standard output → input):
 *   Left-to-right by rank.  After every 4th rank column the layout wraps to a new row.
 */

import { WorkflowNode, WorkflowConnection } from '@flow-studio/types';
import type { ProcessElementPort } from '@flow-studio/types';

export interface LayoutOptions {
  direction: 'horizontal' | 'vertical';
  nodeSpacing: { horizontal: number; vertical: number };
  rankSpacing: number;
  startPosition: { x: number; y: number };
}

const DEFAULT_OPTIONS: LayoutOptions = {
  direction: 'horizontal',
  nodeSpacing: { horizontal: 320, vertical: 180 },
  rankSpacing: 320,
  startPosition: { x: 80, y: 120 },
};

/**
 * Any targetHandle in this set triggers satellite handling.
 * The *placement direction* is then derived from the port's `position` field,
 * not hard-coded here.
 */
const SATELLITE_TARGET_HANDLES = new Set([
  'llm', 'memory', 'tool', 'knowledgebase',
  'team-leader', 'extends',
  'component',
]);

const COLS_PER_ROW = 4;

// ─── Port helpers ─────────────────────────────────────────────────────────────

/**
 * Find the target port on a satellite node and return its `position`.
 * Falls back to 'left' if the port definition cannot be found.
 */
function getTargetPortPosition(
  satelliteNode: WorkflowNode,
  targetHandle: string
): 'top' | 'right' | 'bottom' | 'left' {
  // System ports with known positions — no need to look up from data
  if (targetHandle === 'component') return 'top';

  const ports = (satelliteNode.data?.processElement?.ports ?? []) as ProcessElementPort[];
  const port = ports.find(
    (p) => p.portKey === targetHandle && p.portType === 'input'
  );
  return port?.position ?? 'left';
}

/**
 * Given the port's position on the satellite, return the (dx, dy) offset
 * that places the satellite near the parent so the wire is as short as possible.
 *
 * idx  = 0-based index among sibling satellites going in the same direction
 *         (used to spread them out so they don't overlap).
 */
function getSatelliteOffset(
  portPosition: 'top' | 'right' | 'bottom' | 'left',
  idx: number,
  spacing: { horizontal: number; vertical: number }
): { dx: number; dy: number } {
  switch (portPosition) {
    // Port on BOTTOM of satellite → satellite goes ABOVE parent
    case 'bottom':
      return {
        dx: idx * spacing.horizontal * 0.55,
        dy: -spacing.vertical,
      };

    // Port on TOP of satellite → satellite goes BELOW parent
    case 'top':
      return {
        dx: idx * spacing.horizontal * 0.55,
        dy: spacing.vertical,
      };

    // Port on LEFT of satellite → satellite goes to the LEFT of parent
    case 'left':
      return {
        dx: -(idx + 1) * spacing.horizontal,
        dy: idx * spacing.vertical * 0.5,
      };

    // Port on RIGHT of satellite → satellite goes to the RIGHT of parent
    case 'right':
    default:
      return {
        dx: (idx + 1) * spacing.horizontal,
        dy: idx * spacing.vertical * 0.5,
      };
  }
}

// ─── Graph helpers ────────────────────────────────────────────────────────────

function buildFlowAdjacency(
  nodes: WorkflowNode[],
  flowConns: WorkflowConnection[]
): { children: Map<string, string[]>; inDegree: Map<string, number> } {
  const children = new Map<string, string[]>();
  const inDegree = new Map<string, number>();
  nodes.forEach((n) => { children.set(n.id, []); inDegree.set(n.id, 0); });

  for (const c of flowConns) {
    children.get(c.source)?.push(c.target);
    inDegree.set(c.target, (inDegree.get(c.target) ?? 0) + 1);
  }
  return { children, inDegree };
}

/** Longest-path ranking via Kahn's topological sort. */
function assignRanks(
  nodes: WorkflowNode[],
  flowConns: WorkflowConnection[]
): Map<string, number> {
  const { children, inDegree } = buildFlowAdjacency(nodes, flowConns);
  const ranks = new Map<string, number>();

  const queue: string[] = [];
  inDegree.forEach((deg, id) => {
    if (deg === 0) { queue.push(id); ranks.set(id, 0); }
  });

  while (queue.length > 0) {
    const id = queue.shift()!;
    const r = ranks.get(id) ?? 0;
    for (const child of (children.get(id) ?? [])) {
      const next = r + 1;
      if (!ranks.has(child) || ranks.get(child)! < next) ranks.set(child, next);
      const deg = (inDegree.get(child) ?? 1) - 1;
      inDegree.set(child, deg);
      if (deg <= 0) queue.push(child);
    }
  }

  nodes.forEach((n) => { if (!ranks.has(n.id)) ranks.set(n.id, 0); });
  return ranks;
}

/** Barycenter heuristic — orders nodes within a rank to minimise edge crossings. */
function orderWithinRanks(
  rankGroups: Map<number, WorkflowNode[]>,
  maxRank: number,
  children: Map<string, string[]>
): Map<number, WorkflowNode[]> {
  const ordered = new Map<number, WorkflowNode[]>();

  for (let r = 0; r <= maxRank; r++) {
    const group = rankGroups.get(r) ?? [];
    if (r === 0) { ordered.set(r, group); continue; }

    const prevPos = new Map<string, number>();
    (ordered.get(r - 1) ?? []).forEach((n, i) => prevPos.set(n.id, i));

    const scored = group.map((n) => {
      const parents = Array.from(children.entries())
        .filter(([, cs]) => cs.includes(n.id))
        .map(([pid]) => pid);
      const score = parents.length === 0
        ? Infinity
        : parents.reduce((s, pid) => s + (prevPos.get(pid) ?? 0), 0) / parents.length;
      return { n, score };
    });
    scored.sort((a, b) => a.score - b.score);
    ordered.set(r, scored.map((x) => x.n));
  }
  return ordered;
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function autoLayoutNodes(
  nodes: WorkflowNode[],
  connections: WorkflowConnection[],
  options: Partial<LayoutOptions> = {}
): WorkflowNode[] {
  if (nodes.length === 0) return nodes;

  const opts = { ...DEFAULT_OPTIONS, ...options };

  // ── Step 1: Classify connections ──────────────────────────────────────────
  const satConns = connections.filter((c) =>
    SATELLITE_TARGET_HANDLES.has(c.targetHandle ?? '')
  );
  const flowConns = connections.filter((c) =>
    !SATELLITE_TARGET_HANDLES.has(c.targetHandle ?? '')
  );

  const satelliteNodeIds = new Set(satConns.map((c) => c.target));

  // Map: satellite node id → { parentId, targetHandle }
  const satelliteInfo = new Map<string, { parentId: string; targetHandle: string }>(
    satConns.map((c) => [c.target, { parentId: c.source, targetHandle: c.targetHandle ?? '' }])
  );

  const flowNodes = nodes.filter((n) => !satelliteNodeIds.has(n.id));
  const nodeById = new Map(nodes.map((n) => [n.id, n]));

  // ── Step 2: Rank flow nodes (Rule 2 – left → right by depth) ─────────────
  const ranks = assignRanks(flowNodes, flowConns);
  const maxRank = flowNodes.length > 0
    ? Math.max(...Array.from(ranks.values()))
    : 0;

  const rankGroups = new Map<number, WorkflowNode[]>();
  for (let r = 0; r <= maxRank; r++) rankGroups.set(r, []);
  flowNodes.forEach((n) => rankGroups.get(ranks.get(n.id) ?? 0)!.push(n));

  const { children } = buildFlowAdjacency(flowNodes, flowConns);
  const orderedRanks = orderWithinRanks(rankGroups, maxRank, children);

  // ── Step 3: Compute wrapped-row Y offsets (break after COLS_PER_ROW = 4) ──
  const numRowBlocks = Math.ceil((maxRank + 1) / COLS_PER_ROW);

  // Max nodes-in-any-rank-cell per row-block (determines block height)
  const rowBlockDepth = new Array(numRowBlocks).fill(1);
  for (let r = 0; r <= maxRank; r++) {
    const rb = Math.floor(r / COLS_PER_ROW);
    rowBlockDepth[rb] = Math.max(rowBlockDepth[rb], (orderedRanks.get(r) ?? []).length);
  }

  const ROW_BLOCK_GAP = opts.nodeSpacing.vertical * 0.6;
  const rowBlockStartY: number[] = [opts.startPosition.y];
  for (let rb = 1; rb < numRowBlocks; rb++) {
    rowBlockStartY.push(
      rowBlockStartY[rb - 1] +
      rowBlockDepth[rb - 1] * opts.nodeSpacing.vertical +
      ROW_BLOCK_GAP
    );
  }

  // ── Step 4: Assign positions to flow nodes ────────────────────────────────
  const positions = new Map<string, { x: number; y: number }>();

  for (let r = 0; r <= maxRank; r++) {
    const group = orderedRanks.get(r) ?? [];
    const rb = Math.floor(r / COLS_PER_ROW);
    const col = r % COLS_PER_ROW;

    group.forEach((node, idx) => {
      positions.set(node.id, {
        x: opts.startPosition.x + col * opts.nodeSpacing.horizontal,
        y: rowBlockStartY[rb] + idx * opts.nodeSpacing.vertical,
      });
    });
  }

  // ── Step 5: Place satellite nodes using port-position rules (Rule 1) ──────
  //
  // Group siblings by (parentId + portPosition) so they spread out correctly.
  const siblingCounters = new Map<string, number>(); // key = `${parentId}:${portPosition}`

  for (const node of nodes) {
    if (!satelliteNodeIds.has(node.id)) continue;

    const info = satelliteInfo.get(node.id);
    const parentId = info?.parentId;
    const targetHandle = info?.targetHandle ?? '';
    const parentPos = parentId ? positions.get(parentId) : null;

    if (parentPos) {
      // Determine the direction to place this satellite based on its port position
      const portPos = getTargetPortPosition(node, targetHandle);
      const counterKey = `${parentId}:${portPos}`;
      const idx = siblingCounters.get(counterKey) ?? 0;
      siblingCounters.set(counterKey, idx + 1);

      const { dx, dy } = getSatelliteOffset(portPos, idx, opts.nodeSpacing);
      positions.set(node.id, { x: parentPos.x + dx, y: parentPos.y + dy });
    } else {
      // Orphaned satellite — park at bottom-right (handled below with other orphans)
      positions.set(node.id, null as any); // placeholder; resolved below
    }
  }

  // ── Step 6: Park orphaned nodes at bottom-right of the canvas ────────────
  //
  // "Orphaned" = any node that still has no position (satellite with no parent,
  // or isolated flow node that BFS never reached).
  //
  const orphans = nodes.filter((n) => !positions.has(n.id) || positions.get(n.id) === null);

  if (orphans.length > 0) {
    // Find the bottom-right corner of all positioned nodes
    let maxX = opts.startPosition.x;
    let maxY = opts.startPosition.y;
    positions.forEach((pos) => {
      if (pos) {
        if (pos.x > maxX) maxX = pos.x;
        if (pos.y > maxY) maxY = pos.y;
      }
    });

    const orphanStartX = maxX + opts.nodeSpacing.horizontal;
    const orphanStartY = maxY + opts.nodeSpacing.vertical;

    orphans.forEach((node, idx) => {
      positions.set(node.id, {
        x: orphanStartX + (idx % COLS_PER_ROW) * opts.nodeSpacing.horizontal,
        y: orphanStartY + Math.floor(idx / COLS_PER_ROW) * opts.nodeSpacing.vertical,
      });
    });
  }

  // Suppress unused-variable warning — nodeById kept for potential future use
  void nodeById;

  return nodes.map((node) => ({
    ...node,
    position: positions.get(node.id) ?? node.position,
  }));
}

// ─── Utility exports ──────────────────────────────────────────────────────────

export function calculateBoundingBox(nodes: WorkflowNode[]): {
  minX: number; minY: number; maxX: number; maxY: number; width: number; height: number;
} {
  if (nodes.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  const xs = nodes.map((n) => n.position.x);
  const ys = nodes.map((n) => n.position.y);
  const [minX, maxX] = [Math.min(...xs), Math.max(...xs)];
  const [minY, maxY] = [Math.min(...ys), Math.max(...ys)];
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

export function getRecommendedZoom(
  nodes: WorkflowNode[],
  viewportWidth: number,
  viewportHeight: number
): number {
  const bbox = calculateBoundingBox(nodes);
  if (bbox.width === 0 || bbox.height === 0) return 1;
  const pad = 0.2;
  const zoomX = viewportWidth / (bbox.width * (1 + pad * 2));
  const zoomY = viewportHeight / (bbox.height * (1 + pad * 2));
  return Math.max(0.1, Math.min(zoomX, zoomY, 1.5));
}
